import React, { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import jsQR from "jsqr";
import { Modal } from "antd";

const SCAN_INTERVAL_MS = 300;
const RESUME_DELAY_MS = 1500;

const QRCodeScanner = ({ open, onClose, onScan, title = "Scan QR Code" }) => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const intervalRef = useRef(null);
  const pausedRef = useRef(false);
  const [error, setError] = useState("");
  const [scanMessage, setScanMessage] = useState("");

  if (!canvasRef.current) {
    canvasRef.current = document.createElement("canvas");
  }

  const scanFrame = useCallback(() => {
    if (pausedRef.current) return;

    const video = webcamRef.current?.video;
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) return;

    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const result = jsQR(imageData.data, imageData.width, imageData.height);
    if (result?.data) {
      pausedRef.current = true;
      const isValid = onScan(result.data);
      if (isValid) {
        setScanMessage("");
      } else {
        setScanMessage("Incorrect QR code. Please try again.");
        setTimeout(() => {
          pausedRef.current = false;
        }, RESUME_DELAY_MS);
      }
    }
  }, [onScan]);

  useEffect(() => {
    if (open) {
      setError("");
      setScanMessage("");
      pausedRef.current = false;
      intervalRef.current = setInterval(scanFrame, SCAN_INTERVAL_MS);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open, scanFrame]);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Webcam
        audio={false}
        ref={webcamRef}
        videoConstraints={{ facingMode: "environment" }}
        onUserMediaError={() =>
          setError("Unable to access camera. Please check camera permissions.")
        }
        style={{ width: "100%", borderRadius: 8 }}
      />
      {error ? (
        <p className="text-danger mt-2 mb-0">{error}</p>
      ) : scanMessage ? (
        <p className="text-danger mt-2 mb-0">{scanMessage}</p>
      ) : (
        <p className="text-muted mt-2 mb-0">
          Point your camera at the location QR code.
        </p>
      )}
    </Modal>
  );
};

export default QRCodeScanner;
